import React, { useState } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { FaClock, FaCalendar, FaSave, FaCog, FaInfoCircle, FaQrcode, FaCreditCard, FaTruck, FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';

const AdminSettings = () => {
  const { settings, updateSettings, updateQRCodeSettings, updateDeliverySettings, getNextOrderDeadline, getNextDeliveryDate } = useSettings();
  const [formData, setFormData] = useState({
    orderCutoffTime: settings.orderCutoffTime,
    isOrderingOpen: settings.isOrderingOpen,
    whatsappGroupLink: settings.whatsappGroupLink || '',
    whatsappSupportNumber: settings.contactInfo?.whatsappSupportNumber || '9711533444'
  });
  const [qrCodeData, setQrCodeData] = useState({
    qrCodeImage: settings.paymentSettings?.qrCodeImage || '/qr-code-payment.png',
    qrCodeInstructions: settings.paymentSettings?.qrCodeInstructions || 'Scan this QR code with your preferred payment app (Paytm, Google Pay, PhonePe, etc.) and enter the order amount.',
    paymentNote: settings.paymentSettings?.paymentNote || 'Food Order'
  });
  const [deliveryData, setDeliveryData] = useState({
    enableDeliveryFee: settings.deliverySettings?.enableDeliveryFee || false,
    deliveryFee: settings.deliverySettings?.deliveryFee || 50.00,
    freeDeliveryThreshold: settings.deliverySettings?.freeDeliveryThreshold || 500.00,
    freeDeliveryEnabled: settings.deliverySettings?.freeDeliveryEnabled || true
  });
  const [loading, setLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleQRCodeChange = (e) => {
    const { name, value } = e.target;
    setQrCodeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeliveryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeliveryData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = await updateSettings(formData);
      if (success) {
        toast.success('Settings updated successfully!');
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleQRCodeSubmit = async (e) => {
    e.preventDefault();
    setQrLoading(true);

    try {
      const success = await updateQRCodeSettings(qrCodeData);
      if (success) {
        toast.success('QR Code settings updated successfully!');
      } else {
        toast.error('Failed to update QR code settings');
      }
    } catch (error) {
      toast.error('Failed to update QR code settings');
    } finally {
      setQrLoading(false);
    }
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();
    setDeliveryLoading(true);

    try {
      const success = await updateDeliverySettings(deliveryData);
      if (success) {
        toast.success('Delivery settings updated successfully!');
      } else {
        toast.error('Failed to update delivery settings');
      }
    } catch (error) {
      toast.error('Failed to update delivery settings');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const nextDeadline = getNextOrderDeadline();
  const nextDelivery = getNextDeliveryDate();

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <FaCog className="me-2" />
          Admin Settings
        </h1>
      </div>

      <div className="row">
        <div className="col-lg-8">
          {/* Order Management Settings */}
          <div className="card mb-4">
            <div className="card-body">
              <h3 className="card-title mb-4">
                <FaCalendar className="me-2" />
                Order Management Settings
              </h3>
              
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="orderCutoffTime">
                        <FaClock className="me-2" />
                        Daily Order Cut-off Time
                      </label>
                      <input
                        type="time"
                        id="orderCutoffTime"
                        name="orderCutoffTime"
                        className="form-control"
                        value={formData.orderCutoffTime}
                        onChange={handleInputChange}
                        required
                      />
                      <small className="form-text text-secondary">
                        Orders will not be accepted after this time each day
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="isOrderingOpen"
                          name="isOrderingOpen"
                          className="form-check-input"
                          checked={formData.isOrderingOpen}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="isOrderingOpen">
                          Enable Ordering
                        </label>
                      </div>
                      <small className="form-text text-secondary">
                        Toggle to completely disable ordering system
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="whatsappGroupLink">
                    <FaWhatsapp className="me-2" />
                    WhatsApp Group Link
                  </label>
                  <input
                    type="url"
                    id="whatsappGroupLink"
                    name="whatsappGroupLink"
                    className="form-control"
                    value={formData.whatsappGroupLink || ''}
                    onChange={handleInputChange}
                    placeholder="https://chat.whatsapp.com/..."
                    required
                  />
                  <small className="form-text text-secondary">
                    This link will be shown on the landing page for users to join your WhatsApp group.
                  </small>
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="whatsappSupportNumber">
                    <FaWhatsapp className="me-2" />
                    WhatsApp Support Number
                  </label>
                  <input
                    type="tel"
                    id="whatsappSupportNumber"
                    name="whatsappSupportNumber"
                    className="form-control"
                    value={formData.whatsappSupportNumber || ''}
                    onChange={handleInputChange}
                    placeholder="9711533444"
                    pattern="[0-9]{10,15}"
                    required
                  />
                  <small className="form-text text-secondary">
                    This number will be used for customer support via WhatsApp. Users will be redirected to chat with this number.
                  </small>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* QR Code Payment Settings */}
          <div className="card mb-4">
            <div className="card-body">
              <h3 className="card-title mb-4">
                <FaQrcode className="me-2" />
                QR Code Payment Settings
              </h3>
              
              <form onSubmit={handleQRCodeSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="qrCodeImage">
                        <FaQrcode className="me-2" />
                        QR Code Image Path
                      </label>
                      <input
                        type="text"
                        id="qrCodeImage"
                        name="qrCodeImage"
                        className="form-control"
                        value={qrCodeData.qrCodeImage}
                        onChange={handleQRCodeChange}
                        placeholder="/qr-code-payment.png"
                        required
                      />
                      <small className="form-text text-secondary">
                        Path to QR code image in public folder (e.g., /qr-code-payment.png)
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="paymentNote">
                        <FaCreditCard className="me-2" />
                        Payment Note Format
                      </label>
                      <input
                        type="text"
                        id="paymentNote"
                        name="paymentNote"
                        className="form-control"
                        value={qrCodeData.paymentNote}
                        onChange={handleQRCodeChange}
                        placeholder="Food Order"
                        required
                      />
                      <small className="form-text text-secondary">
                        Default note for payment transactions
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-4">
                  <label htmlFor="qrCodeInstructions">
                    <FaInfoCircle className="me-2" />
                    Payment Instructions
                  </label>
                  <textarea
                    id="qrCodeInstructions"
                    name="qrCodeInstructions"
                    className="form-control"
                    rows="3"
                    value={qrCodeData.qrCodeInstructions}
                    onChange={handleQRCodeChange}
                    placeholder="Scan this QR code with your preferred payment app..."
                    required
                  />
                  <small className="form-text text-secondary">
                    Instructions shown to customers during checkout
                  </small>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="submit"
                    className="btn btn-success btn-lg"
                    disabled={qrLoading}
                  >
                    {qrLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaQrcode className="me-2" />
                        Save QR Code Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Delivery Settings */}
          <div className="card">
            <div className="card-body">
              <h3 className="card-title mb-4">
                <FaTruck className="me-2" />
                Delivery Settings
              </h3>
              
              <form onSubmit={handleDeliverySubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="enableDeliveryFee"
                          name="enableDeliveryFee"
                          className="form-check-input"
                          checked={deliveryData.enableDeliveryFee}
                          onChange={handleDeliveryChange}
                        />
                        <label className="form-check-label" htmlFor="enableDeliveryFee">
                          Enable Delivery Fee
                        </label>
                      </div>
                      <small className="form-text text-secondary">
                        Toggle to enable/disable delivery fees for orders
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="freeDeliveryEnabled"
                          name="freeDeliveryEnabled"
                          className="form-check-input"
                          checked={deliveryData.freeDeliveryEnabled}
                          onChange={handleDeliveryChange}
                        />
                        <label className="form-check-label" htmlFor="freeDeliveryEnabled">
                          Enable Free Delivery Threshold
                        </label>
                      </div>
                      <small className="form-text text-secondary">
                        Allow free delivery for orders above threshold
                      </small>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="deliveryFee">
                        <FaTruck className="me-2" />
                        Delivery Fee (₹)
                      </label>
                      <input
                        type="number"
                        id="deliveryFee"
                        name="deliveryFee"
                        className="form-control"
                        value={deliveryData.deliveryFee}
                        onChange={handleDeliveryChange}
                        min="0"
                        step="0.01"
                        disabled={!deliveryData.enableDeliveryFee}
                        required
                      />
                      <small className="form-text text-secondary">
                        Standard delivery fee for orders
                      </small>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="form-group mb-4">
                      <label htmlFor="freeDeliveryThreshold">
                        <FaTruck className="me-2" />
                        Free Delivery Threshold (₹)
                      </label>
                      <input
                        type="number"
                        id="freeDeliveryThreshold"
                        name="freeDeliveryThreshold"
                        className="form-control"
                        value={deliveryData.freeDeliveryThreshold}
                        onChange={handleDeliveryChange}
                        min="0"
                        step="0.01"
                        disabled={!deliveryData.freeDeliveryEnabled}
                        required
                      />
                      <small className="form-text text-secondary">
                        Orders above this amount get free delivery
                      </small>
                    </div>
                  </div>
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button
                    type="submit"
                    className="btn btn-info btn-lg"
                    disabled={deliveryLoading}
                  >
                    {deliveryLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaTruck className="me-2" />
                        Save Delivery Settings
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-4">
                <FaInfoCircle className="me-2" />
                Current Schedule
              </h4>
              
              <div className="mb-4">
                <h5>Next Order Deadline</h5>
                <div className="p-3 bg-light rounded">
                  <div className="d-flex align-items-center mb-2">
                    <FaClock className="text-primary me-2" />
                    <strong>{nextDeadline.toLocaleDateString('en-GB')}</strong>
                  </div>
                  <p className="mb-0 text-secondary">
                    {nextDeadline.toLocaleTimeString()} ({settings.orderCutoffTime})
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <h5>Next Delivery Date</h5>
                <div className="p-3 bg-light rounded">
                  <div className="d-flex align-items-center mb-2">
                    <FaCalendar className="text-success me-2" />
                    <strong>{nextDelivery.toLocaleDateString('en-GB')}</strong>
                  </div>
                  <p className="mb-0 text-secondary">
                    {nextDelivery.toLocaleDateString('en-US', { weekday: 'long' })}
                  </p>
                </div>
              </div>

              <div className="alert alert-info">
                <h6>How it works:</h6>
                <ul className="mb-0 small">
                  <li>Orders close daily at {settings.orderCutoffTime}</li>
                  <li>Users see real-time countdown to deadline</li>
                  <li>Settings apply immediately after saving</li>
                </ul>
              </div>

              <div className="alert alert-warning">
                <h6>QR Code Setup:</h6>
                <ul className="mb-0 small">
                  <li>Add your QR code image to the public folder</li>
                  <li>Update the image path in QR Code settings</li>
                  <li>Test the QR code on the checkout page</li>
                  <li>Ensure the image is accessible via the web</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings; 